import {
	SET_COMPANIES,
	SET_EMPLOYEES,
	SET_POSITIONS,
	SET_COMPANIES_BY_PERMISSION,
	SET_COMPANIES_BY_FEATURE,
	SET_SERVICE_DEPARTMENTS,
	SET_PROCESS_TRIGGER_RESPONSE
} from './company-mutation-types';
import api from '@/services/api-service';
import buildRoute from '@/composables/build-route';
import Vue from 'vue';

export default {
	strict: process.env.NODE_ENV !== 'production',
	name: 'company',
	namespaced: true,
	state: {
		companies: [],
		employees: [],
		positions: [],
		companiesByPermission: {},
		companiesByFeature: {},
		serviceDepartments: undefined,
		processTriggerResponse: {}
	},
	mutations: {
		[SET_COMPANIES](state, companies) {
			state.companies = companies;
		},
		[SET_EMPLOYEES](state, employees) {
			if (employees.page === 1) {
				state.employees = employees;
			} else {
				employees.items = state.employees.items.concat(employees.items);
				state.employees = employees;
			}
		},
		[SET_POSITIONS](state, positions) {
			state.positions = positions;
		},
		[SET_COMPANIES_BY_PERMISSION](state, { permissionId, companies }) {
			Vue.set(state.companiesByPermission, permissionId, companies);
		},
		[SET_COMPANIES_BY_FEATURE](state, { featureId, companies }) {
			Vue.set(state.companiesByFeature, featureId, companies);
		},
		[SET_SERVICE_DEPARTMENTS](state, serviceDepartments) {
			state.serviceDepartments = serviceDepartments;
		},
		[SET_PROCESS_TRIGGER_RESPONSE](state, sendMessage) {
			state.processTriggerResponse = sendMessage;
		}
	},
	actions: {
		// actions let us get to ({ state, getters, commit, dispatch }) {
		async getCompaniesAction({ commit }, currentCompanyId) {
			const companies = await api.get(
				buildRoute('/dealership/company/{companyId}/companies', {
					companyId: currentCompanyId
				})
			);
			commit(SET_COMPANIES, companies);
		},
		async getEmployeesAction(
			{ commit },
			{ searchText, companyIds, currentCompanyId, page, maxCount }
		) {
			const employees = await api.get(
				buildRoute('/dealership/company/{currentCompanyId}/employees', {
					query: {
						searchText: searchText,
						companies: companyIds,
						page: page,
						maxCount: maxCount
					},
					currentCompanyId
				})
			);
			commit(SET_EMPLOYEES, employees);
		},
		async getPositionsAction({ commit }) {
			const positions = await api.get(
				buildRoute('/globallookups/positions')
			);
			commit(SET_POSITIONS, positions);
		},
		async getCompaniesByPermissionAction(
			{ commit },
			{ currentCompanyId, permissionId, hasAnyPermission = false }
		) {
			let objKey = permissionId;
			const searchParameters = {
				companyId: currentCompanyId,
				query: {
					permissionId: permissionId,
					hasAnyPermission: hasAnyPermission
				}
			};
			const companies = await api.get(
				buildRoute(
					'/dealership/company/{companyId}/companies/permissionlist',
					searchParameters
				)
			);

			if (Array.isArray(permissionId)) {
				objKey = Array.prototype.join.call(permissionId);
			}

			commit(SET_COMPANIES_BY_PERMISSION, {
				permissionId: objKey,
				companies: companies
			});
		},
		async getCompaniesByFeatureAction(
			{ commit },
			{ currentCompanyId, featureId }
		) {
			let objKey = featureId;
			const searchParameters = {
				companyId: currentCompanyId,
				query: {
					featureId: featureId
				}
			};
			const companies = await api.get(
				buildRoute(
					'/dealership/company/{companyId}/companies/featurelist',
					searchParameters
				)
			);
			if (Array.isArray(featureId)) {
				objKey = Array.prototype.join.call(featureId);
			}

			commit(SET_COMPANIES_BY_FEATURE, {
				featureId: objKey,
				companies: companies
			});
		},
		async getServiceDepartmentsAction(
			{ commit },
			{ companyId, locationId }
		) {
			const departments = await api.get(
				buildRoute(
					'/dealership/company/{companyId}/departments/service',
					{
						companyId,
						query: {
							locationId
						}
					}
				)
			);

			commit(SET_SERVICE_DEPARTMENTS, departments);
		},
		async sendProcessTriggerAction(
			{ commit },
			{ companyId, processName, data }
		) {
			const sendMessage = await api.post(
				buildRoute('/dealership/company/{companyId}/process/trigger', {
					companyId
				}),
				{
					processName: processName,
					data: data
				}
			);

			commit(SET_PROCESS_TRIGGER_RESPONSE, sendMessage);
		},
		async saveScheduledTaskAction(
			// eslint-disable-next-line no-unused-vars
			{ commit },
			{
				dealershipGroupGuid,
				aspnettaskId,
				companyId,
				runFrequency,
				frequencyValue,
				enabled,
				startWhen,
				parameter
			}
		) {
			await api.post(
				buildRoute('/users/company/{companyId}/scheduletask', {
					companyId: companyId
				}),
				{
					dealershipGroupGuid,
					aspnettaskId,
					companyId,
					runFrequency,
					frequencyValue,
					enabled,
					startWhen,
					parameter
				}
			);
		}
	},
	getters: {
		companies: state => state.companies,
		companyIds: state =>
			state.companies &&
			state.companies.items &&
			state.companies.items.map(c => c.id),
		companyEmployees: state => state.employees,
		companyPositions: state => state.positions,
		companiesByPermission: state => permissionId => {
			return state.companiesByPermission[permissionId];
		},
		companiesByFeature: state => featureId => {
			return state.companiesByFeature[featureId];
		},
		serviceDepartments: state => state.serviceDepartments
	}
};
